import { Slider } from '@/components/ui/slider';
import {
  SUBTITLE_ANIMATION_OPTIONS,
  SUBTITLE_COLOR_PRESETS,
  SUBTITLE_FONT_OPTIONS,
} from '@/lib/subtitles/subtitleStyleConfig';
import {
  SUBTITLE_MAX_FONT_SIZE,
  SUBTITLE_MAX_OUTLINE_WIDTH,
  SUBTITLE_MIN_FONT_SIZE,
} from '@/lib/videoSubtitlePreview';

/**
 * Shared subtitle typography controls (font, colors, motion) for editor panels.
 */
export default function SubtitleLookControls({
  style,
  onChange,
  previewFontPx = null,
  showSnapToGrid = false,
  snapToGrid = false,
  onSnapToGridChange,
  testIdPrefix = 'subtitle',
}) {
  const patch = (partial) => onChange?.({ ...style, ...partial });

  return (
    <div className="space-y-2.5" data-testid={`${testIdPrefix}-look-controls`}>
      <div className="space-y-1">
        <div className="flex justify-between text-[11px] gap-2" style={{ color: 'var(--text-secondary)' }}>
          <span>Size (export)</span>
          <span className="tabular-nums shrink-0">
            {style.fontSize}px export
            {previewFontPx > 0 ? (
              <span style={{ color: 'var(--text-tertiary)' }}>
                {' '}
                · {Math.round(previewFontPx)}px preview
              </span>
            ) : null}
          </span>
        </div>
        <Slider
          value={[style.fontSize]}
          min={SUBTITLE_MIN_FONT_SIZE}
          max={SUBTITLE_MAX_FONT_SIZE}
          step={1}
          onValueChange={([v]) => {
            const next = Math.round(Number(v));
            if (!Number.isFinite(next)) return;
            patch({ fontSize: next });
          }}
          data-testid={`${testIdPrefix}-font-size-slider`}
        />
      </div>
      <div className="space-y-1">
        <div className="flex justify-between text-[11px]" style={{ color: 'var(--text-secondary)' }}>
          <span>Outline</span>
          <span>{style.outlineWidth}px</span>
        </div>
        <Slider
          value={[style.outlineWidth]}
          min={0}
          max={SUBTITLE_MAX_OUTLINE_WIDTH}
          step={0.5}
          onValueChange={([v]) => {
            const next = Number(v);
            if (!Number.isFinite(next)) return;
            patch({ outlineWidth: next });
          }}
        />
      </div>
      {showSnapToGrid ? (
        <label className="flex items-center gap-2 text-[11px] cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
          <input
            type="checkbox"
            className="rounded"
            checked={snapToGrid}
            onChange={(e) => onSnapToGridChange?.(e.target.checked)}
            data-testid={`${testIdPrefix}-snap-grid-toggle`}
          />
          Snap to grid
        </label>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
          <span>Font</span>
          <select
            value={style.fontFamily || 'Arial'}
            onChange={(ev) => patch({ fontFamily: ev.target.value })}
            className="h-7 rounded border px-1.5 text-[11px] max-w-[9rem]"
            style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
            data-testid={`${testIdPrefix}-font-family-select`}
          >
            {SUBTITLE_FONT_OPTIONS.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
          <span>Motion</span>
          <select
            value={style.animation || 'none'}
            onChange={(ev) => patch({ animation: ev.target.value })}
            className="h-7 rounded border px-1.5 text-[11px] max-w-[9rem]"
            style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
          >
            {SUBTITLE_ANIMATION_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="space-y-1">
        <div className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>Text color</div>
        <div className="flex flex-wrap gap-1.5">
          {SUBTITLE_COLOR_PRESETS.map((c) => (
            <button
              key={`text-${c}`}
              type="button"
              aria-label={`Text color ${c}`}
              className="h-6 w-6 rounded border shrink-0"
              style={{
                background: c,
                borderColor: style.color === c ? '#38bdf8' : 'var(--glass-border)',
                boxShadow: style.color === c ? '0 0 0 1px #38bdf8' : undefined,
              }}
              onClick={() => patch({ color: c })}
            />
          ))}
          <input
            type="color"
            value={style.color && style.color.startsWith('#') ? style.color : '#ffffff'}
            onChange={(ev) => patch({ color: ev.target.value })}
            className="h-6 w-8 cursor-pointer rounded border p-0"
            style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)' }}
            title="Custom text color"
          />
        </div>
      </div>
      <div className="space-y-1">
        <div className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>Outline color</div>
        <div className="flex flex-wrap gap-1.5">
          {SUBTITLE_COLOR_PRESETS.map((c) => (
            <button
              key={`outline-${c}`}
              type="button"
              aria-label={`Outline color ${c}`}
              className="h-6 w-6 rounded border shrink-0"
              style={{
                background: c,
                borderColor: style.outlineColor === c ? '#38bdf8' : 'var(--glass-border)',
                boxShadow: style.outlineColor === c ? '0 0 0 1px #38bdf8' : undefined,
              }}
              onClick={() => patch({ outlineColor: c })}
            />
          ))}
          <input
            type="color"
            value={style.outlineColor && style.outlineColor.startsWith('#') ? style.outlineColor : '#000000'}
            onChange={(ev) => patch({ outlineColor: ev.target.value })}
            className="h-6 w-8 cursor-pointer rounded border p-0"
            style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)' }}
            title="Custom outline color"
          />
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex justify-between text-[11px]" style={{ color: 'var(--text-secondary)' }}>
          <span>Glow</span>
          <span>{Math.round(style.shadowBlur || 0)}px</span>
        </div>
        <Slider
          value={[style.shadowBlur || 0]}
          min={0}
          max={20}
          step={1}
          onValueChange={([v]) => {
            const next = Number(v);
            if (!Number.isFinite(next)) return;
            patch({ shadowBlur: next });
          }}
        />
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>Glow color</span>
          <input
            type="color"
            value={style.shadowColor && style.shadowColor.startsWith('#') ? style.shadowColor : '#ffff00'}
            onChange={(ev) => patch({ shadowColor: ev.target.value })}
            className="h-6 w-8 cursor-pointer rounded border p-0"
            style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)' }}
          />
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex justify-between text-[11px]" style={{ color: 'var(--text-secondary)' }}>
          <span>Background box</span>
          <span>{Math.round((style.backgroundOpacity || 0) * 100)}%</span>
        </div>
        <Slider
          value={[Math.round((style.backgroundOpacity || 0) * 100)]}
          min={0}
          max={90}
          step={5}
          onValueChange={([v]) => {
            const next = Number(v);
            if (!Number.isFinite(next)) return;
            patch({ backgroundOpacity: next / 100 });
          }}
        />
        <input
          type="color"
          value={style.backgroundColor && style.backgroundColor.startsWith('#') ? style.backgroundColor : '#000000'}
          onChange={(ev) => patch({ backgroundColor: ev.target.value })}
          className="h-6 w-8 cursor-pointer rounded border p-0"
          style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)' }}
          title="Background color"
        />
      </div>
      <label className="flex items-start gap-2 text-[11px] cursor-pointer select-none" style={{ color: 'var(--text-secondary)' }}>
        <input
          type="checkbox"
          className="mt-0.5"
          checked={style.spokenWordWindow !== false}
          onChange={(ev) => patch({
            spokenWordWindow: ev.target.checked,
            spokenWordLimit: style.spokenWordLimit || 5,
            spokenWordHighlightColor: style.spokenWordHighlightColor || '#d4ff00',
            bold: ev.target.checked ? true : style.bold,
          })}
          data-testid={`${testIdPrefix}-spoken-word-toggle`}
        />
        <span>
          <span className="block" style={{ color: 'var(--text-primary)' }}>Highlight the spoken word</span>
          Show up to five words in capitals. The word being said sits on a yellow box.
        </span>
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none" style={{ color: 'var(--text-secondary)' }}>
          <input
            type="checkbox"
            checked={style.bold}
            onChange={(ev) => patch({ bold: ev.target.checked })}
          />
          Bold
        </label>
        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none" style={{ color: 'var(--text-secondary)' }}>
          <input
            type="checkbox"
            checked={style.italic}
            onChange={(ev) => patch({ italic: ev.target.checked })}
          />
          Italic
        </label>
        <label className="flex items-center gap-1.5 text-[11px] cursor-pointer select-none" style={{ color: 'var(--text-secondary)' }}>
          <input
            type="checkbox"
            checked={style.underline}
            onChange={(ev) => patch({ underline: ev.target.checked })}
          />
          Underline
        </label>
      </div>
      <div className="space-y-1">
        <div className="flex justify-between text-[11px]" style={{ color: 'var(--text-secondary)' }}>
          <span>Letter spacing</span>
          <span>{style.letterSpacing || 0}px</span>
        </div>
        <Slider
          value={[style.letterSpacing || 0]}
          min={-1}
          max={8}
          step={0.5}
          onValueChange={([v]) => {
            const next = Number(v);
            if (!Number.isFinite(next)) return;
            patch({ letterSpacing: next });
          }}
        />
      </div>
    </div>
  );
}
